import React from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { useLoanDetails } from '../../hooks/useLoanDetails';
import { useAuth } from '../../context/AuthContext';
import { PaymentHistoryTimeline } from './PaymentHistoryTimeline';
import { Spinner } from '../common/Spinner';

interface LoanDetailModalProps {
  loanId: string;
  customerName: string;
  onClose: () => void;
}

export function LoanDetailModal({ loanId, customerName, onClose }: LoanDetailModalProps) {
  const { companyId, user, claims } = useAuth();

  const { loanDetails, loading, error } = useLoanDetails({
    companyId: companyId || '',
    loanId,
    // Pass office/broker filters based on user permissions
    officeId: claims?.canSeeAllOffices ? undefined : claims?.officeIds?.[0],
    brokerId: claims?.role === 'broker' ? user?.uid : undefined,
  });

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-end justify-center px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
          onClick={onClose}
        />

        {/* Center modal */}
        <span className="hidden sm:inline-block sm:h-screen sm:align-middle" aria-hidden="true">
          &#8203;
        </span>

        {/* Modal panel */}
        <div className="relative inline-block transform overflow-hidden rounded-lg bg-gray-800 text-left align-bottom shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-4xl sm:align-middle">
          {/* Header */}
          <div className="border-b border-gray-700 bg-gray-800 px-6 py-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-white">Loan Details</h3>
                <p className="mt-1 text-sm text-gray-400">
                  {customerName} - {loanId}
                </p>
              </div>
              <button
                onClick={onClose}
                className="rounded-md bg-gray-700 text-gray-400 hover:text-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-gray-800"
              >
                <span className="sr-only">Close</span>
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="px-6 py-4">
            {loading && (
              <div className="flex items-center justify-center py-12">
                <Spinner size="md" />
                <span className="ml-3 text-gray-400">Loading loan details...</span>
              </div>
            )}

            {error && (
              <div className="rounded-md bg-red-900/50 border border-red-700 p-4">
                <p className="text-sm text-red-300">{error}</p>
              </div>
            )}

            {loanDetails && !loading && !error && (
              <div className="space-y-6">
                {/* Summary cards */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-gray-700 rounded-lg p-4">
                    <p className="text-xs font-medium text-gray-400 uppercase tracking-wide">
                      Lender
                    </p>
                    <p className="mt-2 text-lg font-semibold text-white">
                      {loanDetails.lender}
                    </p>
                  </div>

                  <div className="bg-gray-700 rounded-lg p-4">
                    <p className="text-xs font-medium text-gray-400 uppercase tracking-wide">
                      Settlement Date
                    </p>
                    <p className="mt-2 text-lg font-semibold text-white">
                      {formatDate(loanDetails.settlementDate)}
                    </p>
                  </div>

                  <div className="bg-gray-700 rounded-lg p-4">
                    <p className="text-xs font-medium text-gray-400 uppercase tracking-wide">
                      Loan Amount
                    </p>
                    <p className="mt-2 text-lg font-semibold text-white">
                      {formatMoney(loanDetails.loanAmountCents)}
                    </p>
                  </div>
                </div>

                {/* Commission summary */}
                <div className="border border-gray-700 bg-gray-700/50 rounded-lg p-4">
                  <h4 className="text-sm font-semibold text-white mb-4">
                    Commission Summary
                  </h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div>
                      <p className="text-xs text-gray-400">Gross Upfront</p>
                      <p className="mt-1 text-sm font-medium text-green-400">
                        {formatMoney(loanDetails.summary.grossUpfrontCents)}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-400">Net Upfront</p>
                      <p className="mt-1 text-sm font-medium text-green-500">
                        {formatMoney(loanDetails.summary.netUpfrontCents)}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-400">Gross Trail</p>
                      <p className="mt-1 text-sm font-medium text-blue-400">
                        {formatMoney(loanDetails.summary.grossTrailCents)}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-400">Net Trail</p>
                      <p className="mt-1 text-sm font-medium text-blue-500">
                        {formatMoney(loanDetails.summary.netTrailCents)}
                      </p>
                    </div>
                  </div>

                  {loanDetails.summary.dischargedCommissionCents !== 0 && (
                    <div className="mt-4 pt-4 border-t border-gray-600">
                      <div className="flex items-center justify-between">
                        <p className="text-xs text-gray-400">Discharged Commission</p>
                        <p className="text-sm font-medium text-red-400">
                          {formatMoney(loanDetails.summary.dischargedCommissionCents)}
                        </p>
                      </div>
                    </div>
                  )}

                  <div className="mt-4 pt-4 border-t border-gray-600">
                    <div className="flex items-center justify-between">
                      <p className="text-sm font-semibold text-white">Total Net Commission</p>
                      <p className="text-lg font-bold text-white">
                        {formatMoney(loanDetails.summary.totalNetCents)}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Payment history */}
                <div className="border border-gray-700 bg-gray-700/50 rounded-lg p-4">
                  <h4 className="text-sm font-semibold text-white mb-4">
                    Payment History
                    <span className="ml-2 text-xs font-normal text-gray-400">
                      ({loanDetails.paymentEvents.length} payments)
                    </span>
                  </h4>
                  <div className="mt-4 max-h-96 overflow-y-auto overflow-x-hidden pl-3 pr-4">
                    <PaymentHistoryTimeline events={loanDetails.paymentEvents} />
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="border-t border-gray-700 bg-gray-900 px-6 py-4">
            <div className="flex justify-end">
              <button
                onClick={onClose}
                className="rounded-md bg-gray-700 px-4 py-2 text-sm font-medium text-gray-200 shadow-sm border border-gray-600 hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-gray-900"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function formatMoney(cents: number): string {
  const amount = cents / 100;
  return new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: 'AUD',
    minimumFractionDigits: 2,
  }).format(amount);
}

function formatDate(dateString: string): string {
  if (!dateString || dateString === 'N/A') {
    return dateString || 'N/A';
  }

  try {
    const date = new Date(dateString);
    // Validate date before formatting
    if (isNaN(date.getTime())) {
      return dateString;
    }
    return date.toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch {
    return dateString;
  }
}
